import React, { useRef, useState } from 'react';
import { Box, Button, Card, CardContent, TextField, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, useTheme, alpha, Tooltip, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

interface NodeData {
  id: string;
  name: string;
  description: string;
  startDate: string;
  dueDate: string;
  x: number;
  y: number;
  linkTo?: string; // id of the node this node links to
}

const NODE_WIDTH = 220;
const NODE_HEIGHT = 150; // Increased height
const CANVAS_WIDTH = 1200;
const CANVAS_HEIGHT = 700;

const RoadmapCanvas: React.FC = () => {
  const theme = useTheme();
  const [nodes, setNodes] = useState<NodeData[]>([]);
  const [draggedNodeId, setDraggedNodeId] = useState<string | null>(null);
  const [dragOffset, setDragOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newNode, setNewNode] = useState({ name: '', description: '', startDate: '', dueDate: '' });
  const [linkingFrom, setLinkingFrom] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Add node
  const handleAddNode = () => {
    setNodes([
      ...nodes,
      {
        id: Date.now().toString(),
        name: newNode.name,
        description: newNode.description,
        startDate: newNode.startDate,
        dueDate: newNode.dueDate,
        x: 100 + nodes.length * 40,
        y: 100 + nodes.length * 40,
      },
    ]);
    setNewNode({ name: '', description: '', startDate: '', dueDate: '' });
    setAddDialogOpen(false);
  };

  // Drag logic
  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    const node = nodes.find((n) => n.id === id);
    if (!node) return;
    setDraggedNodeId(id);
    setDragOffset({
      x: e.clientX - node.x,
      y: e.clientY - node.y,
    });
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggedNodeId) return;
    setNodes((prev) =>
      prev.map((n) =>
        n.id === draggedNodeId
          ? { ...n, x: Math.max(0, Math.min(CANVAS_WIDTH - NODE_WIDTH, e.clientX - dragOffset.x)), y: Math.max(0, Math.min(CANVAS_HEIGHT - NODE_HEIGHT, e.clientY - dragOffset.y)) }
          : n
      )
    );
  };
  const handleMouseUp = () => {
    setDraggedNodeId(null);
  };

  // Link logic (one outgoing link per node)
  const handleStartLink = (id: string) => {
    setLinkingFrom(id);
  };
  const handleCompleteLink = (toId: string) => {
    if (linkingFrom && linkingFrom !== toId) {
      setNodes((prev) =>
        prev.map((n) =>
          n.id === linkingFrom ? { ...n, linkTo: toId } : n
        )
      );
    }
    setLinkingFrom(null);
  };
  const handleDeleteNode = (id: string) => {
    setNodes((prev) => prev.filter((n) => n.id !== id).map((n) => n.linkTo === id ? { ...n, linkTo: undefined } : n));
  };

  return (
    <Box>
      {/* Help Message */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Chip
          icon={<LinkIcon color="primary" />}
          label={
            <span>
              <b>How to link nodes:</b> Click <b>Link</b> on a node, then click another node to create an arrow. One link per node.
            </span>
          }
          sx={{ fontSize: '1rem', background: alpha(theme.palette.primary.main, 0.07), color: theme.palette.text.primary, px: 2, py: 1 }}
        />
      </Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>
          Add Node
        </Button>
      </Box>
      <Box
        ref={canvasRef}
        sx={{
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          position: 'relative',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.secondary.main, 0.03)})`,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          overflow: 'auto',
          boxShadow: theme.shadows[2],
          mx: 'auto',
          mb: 4,
          cursor: draggedNodeId ? 'grabbing' : 'default',
        }}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
      >
        {/* SVG for links */}
        <svg width={CANVAS_WIDTH} height={CANVAS_HEIGHT} style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}>
          {nodes.map((n) => {
            if (!n.linkTo) return null;
            const from = n;
            const to = nodes.find((node) => node.id === n.linkTo);
            if (!to) return null;
            const startX = from.x + NODE_WIDTH;
            const startY = from.y + NODE_HEIGHT / 2;
            const endX = to.x;
            const endY = to.y + NODE_HEIGHT / 2;
            // Bezier curve for nice link
            const c1x = startX + 40;
            const c1y = startY;
            const c2x = endX - 40;
            const c2y = endY;
            return (
              <g key={from.id + '-' + to.id}>
                <path
                  d={`M${startX},${startY} C${c1x},${c1y} ${c2x},${c2y} ${endX},${endY}`}
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  fill="none"
                  markerEnd="url(#arrowhead)"
                  opacity={0.7}
                />
              </g>
            );
          })}
          <defs>
            <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="10" refY="3.5" orient="auto" markerUnits="strokeWidth">
              <polygon points="0 0, 10 3.5, 0 7" fill={theme.palette.primary.main} />
            </marker>
          </defs>
        </svg>
        {/* Nodes */}
        {nodes.map((node) => (
          <Box
            key={node.id}
            sx={{
              position: 'absolute',
              left: node.x,
              top: node.y,
              width: NODE_WIDTH,
              height: NODE_HEIGHT,
              zIndex: draggedNodeId === node.id ? 2 : 1,
              cursor: 'grab',
              transition: draggedNodeId === node.id ? 'none' : 'box-shadow 0.2s',
              boxShadow: draggedNodeId === node.id ? theme.shadows[8] : theme.shadows[2],
              border: linkingFrom === node.id ? `3px solid ${theme.palette.secondary.main}` : undefined,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            onMouseDown={(e) => handleMouseDown(e, node.id)}
            onMouseUp={() => linkingFrom && handleCompleteLink(node.id)}
          >
            <Card sx={{ height: '100%', border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 2, background: '#fff', p: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <CardContent sx={{ p: 2, pb: '16px !important', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem', color: theme.palette.primary.main }}>
                    {node.name}
                  </Typography>
                  <Tooltip title="Delete Node">
                    <IconButton size="small" onClick={() => handleDeleteNode(node.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: '0.95rem' }}>
                  {node.description}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, color: theme.palette.info.main }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                    {node.startDate ? `Start: ${node.startDate}` : 'Start: -'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <EventIcon sx={{ fontSize: 18, color: theme.palette.error.main }} />
                  <Typography variant="caption" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
                    {node.dueDate ? `Due: ${node.dueDate}` : 'Due: -'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, mt: 'auto' }}>
                  <Tooltip title={linkingFrom === node.id ? 'Select a node to link to' : 'Link to another node'}>
                    <span>
                      <Button
                        variant={linkingFrom === node.id ? 'contained' : 'outlined'}
                        color={linkingFrom === node.id ? 'secondary' : 'primary'}
                        size="medium"
                        startIcon={<LinkIcon sx={{ fontSize: 28 }} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: '1.1rem',
                          px: 2,
                          py: 1,
                          minWidth: 0,
                          borderRadius: 2,
                          textTransform: 'none',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (linkingFrom === node.id) setLinkingFrom(null);
                          else handleStartLink(node.id);
                        }}
                        disabled={!!linkingFrom && linkingFrom !== node.id}
                      >
                        Link
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Add Node Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Roadmap Node</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            fullWidth
            margin="normal"
            value={newNode.name}
            onChange={(e) => setNewNode({ ...newNode, name: e.target.value })}
          />
          <TextField
            label="Description"
            fullWidth
            margin="normal"
            value={newNode.description}
            onChange={(e) => setNewNode({ ...newNode, description: e.target.value })}
          />
          <TextField
            label="Start Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newNode.startDate}
            onChange={(e) => setNewNode({ ...newNode, startDate: e.target.value })}
          />
          <TextField
            label="Due Date"
            type="date"
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
            value={newNode.dueDate}
            onChange={(e) => setNewNode({ ...newNode, dueDate: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleAddNode} variant="contained" disabled={!newNode.name.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoadmapCanvas; 